import { useMemo } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Boxes, Building, ShieldCheck, Stethoscope, UserCog, Users } from "lucide-react";
import { useCatalog } from "@/context/CatalogContext";
import PermissionsHome from "@/pages/admin/PermissionsHome";

/**
 * CENTRAL ÚNICA DE PERMISSÕES (/admin/permissoes).
 *
 * Ponto administrativo ÚNICO para conceder, revogar e auditar:
 *
 *   - módulos e ações por USUÁRIO (AdminUserDetail — implementação canônica);
 *   - teto de módulos por EMPRESA (AdminCompanyDetail);
 *   - perfis/políticas (AdminProfiles);
 *   - catálogo de módulos (AdminCatalog);
 *   - DIAGNÓSTICO de acesso (mesmo motor do runtime).
 *
 * Não há um segundo formulário de concessão: as abas REUTILIZAM os
 * componentes que já gravam pelo serviço canônico
 * (user_access_service.persist_user_access, com revisão e auditoria).
 */
const SECTIONS = [
  { to: "/admin/permissoes", label: "Painel", icon: ShieldCheck, end: true },
  { to: "/admin/permissoes/usuarios", label: "Usuários e permissões", icon: Users, entry: "gestao_acessos" },
  { to: "/admin/permissoes/clientes", label: "Clientes e módulos", icon: Building, entry: "empresas" },
  { to: "/admin/permissoes/perfis", label: "Perfis e políticas", icon: UserCog, entry: "gestao_acessos" },
  { to: "/admin/permissoes/catalogo", label: "Catálogo de módulos", icon: Boxes, entry: "catalogo_modulos" },
  { to: "/admin/permissoes/diagnostico", label: "Diagnóstico de acesso", icon: Stethoscope, entry: "gestao_acessos" },
];

export default function AdminPermissions() {
  const { isAvailable, loaded } = useCatalog();
  const location = useLocation();

  const visible = useMemo(
    () => SECTIONS.filter((section) => !section.entry || isAvailable(section.entry)),
    [isAvailable]
  );

  const isIndex = location.pathname.replace(/\/$/, "") === "/admin/permissoes";

  return (
    <div className="space-y-4" data-testid="permissions-center">
      <header className="rounded-xl border border-border bg-card p-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
          Central de Permissões
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Único lugar para conceder, revogar e auditar módulos, ações, contratos e perfis.
          Toda alteração é gravada pelo serviço canônico de autorização, com revisão e histórico.
        </p>
      </header>

      {loaded && visible.length <= 1 ? (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700">
          Seu perfil não administra permissões. Fale com a administração da plataforma.
        </p>
      ) : (
        <>
          <nav
            className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1"
            aria-label="Seções da Central de Permissões"
            data-testid="permissions-nav"
          >
            {visible.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 font-medium text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
                data-testid={`permissions-nav-${to.split("/").pop() || "painel"}`}
              >
                <Icon size={15} aria-hidden="true" /> {label}
              </NavLink>
            ))}
          </nav>

          {isIndex ? <PermissionsHome /> : <Outlet />}
        </>
      )}
    </div>
  );
}
